import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import type { Item } from "@instead/shared";
import { api } from "../lib/api.ts";
import { drafts, PICK_OWN_KEY } from "../lib/drafts.ts";
import { todayTitle, useT } from "../lib/i18n.ts";
import { catLabel, metaLine } from "../components/DealCard.tsx";
import { Cover } from "../components/Cover.tsx";
import { Ic } from "../components/Icons.tsx";

type Search = { into?: "deal" | "set"; set?: string; replace?: string };

/**
 * Pick my own (D-25, round 3): compose a set by hand. "Selected · n" is a
 * filter tab; at the max the remaining ticks grey out. The in-progress set
 * autosaves on the device and saves at once under today's date (round 5:
 * no naming step — the name is edited on the set page).
 * ?into=deal edits the current deal; ?replace=<itemId> (with into=deal, or
 * into=set&set=<id>) swaps that one card for the first tap and goes back.
 */
export function PickOwn() {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const search = useSearch({ strict: false }) as Search;
  const intoDeal = search.into === "deal";
  const setId = search.into === "set" ? search.set ?? null : null;
  const replacing = search.replace ?? null;
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const { data: catData } = useQuery({ queryKey: ["categories"], queryFn: api.categories.list });
  const { data: dealData } = useQuery({ queryKey: ["deal"], queryFn: api.deal.get, enabled: intoDeal });
  const { data: sel } = useQuery({ queryKey: ["selections", setId], queryFn: () => api.selections.get(setId!), enabled: !!setId });
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<string>("all");
  const [picked, setPicked] = useState<string[]>(() => (intoDeal || setId ? [] : drafts.read<string[]>(PICK_OWN_KEY) ?? []));
  const [initFromDeal, setInitFromDeal] = useState(!intoDeal);
  const [busy, setBusy] = useState(false);
  const cats = catData?.categories ?? [];
  const max = Math.min(5, settings?.rest.maxCards ?? 5);
  const existing: string[] = intoDeal ? dealData?.deal?.itemIds ?? [] : setId ? sel?.itemIds ?? [] : [];

  if (intoDeal && !replacing && !initFromDeal && dealData) {
    setPicked(dealData.deal?.itemIds ?? []);
    setInitFromDeal(true);
  }

  const { data: page } = useQuery({
    queryKey: ["items", "pick", q, tab],
    queryFn: () => api.items.list({ limit: "200", ...(q.trim() ? { q: q.trim() } : {}), ...(tab !== "all" && tab !== "selected" ? { categoryId: tab } : {}) }),
  });
  const { data: pickedItems } = useQuery({ queryKey: ["items", "byIds", picked.join(",")], queryFn: () => api.items.byIds(picked), enabled: picked.length > 0 });
  const rows: Item[] = tab === "selected" ? pickedItems ?? [] : page?.items ?? [];

  /** Replace mode: the first tap is the whole journey. */
  async function replaceWith(id: string) {
    if (busy) return;
    setBusy(true);
    try {
      const itemIds = existing.map((x) => (x === replacing ? id : x));
      if (intoDeal) {
        const saved = await api.deal.put(itemIds);
        qc.setQueryData(["deal"], saved);
        await nav({ to: "/deal" });
      } else if (setId) {
        const updated = await api.selections.update(setId, { itemIds });
        qc.setQueryData(["selections", setId], updated);
        await qc.invalidateQueries({ queryKey: ["selections"], exact: true });
        await nav({ to: "/set/$id", params: { id: setId } });
      }
    } finally {
      setBusy(false);
    }
  }

  const toggle = (id: string) => {
    if (replacing) { void replaceWith(id); return; }
    const next = picked.includes(id) ? picked.filter((x) => x !== id) : picked.length < max ? [...picked, id] : picked;
    setPicked(next);
    if (!intoDeal) drafts.write(PICK_OWN_KEY, next);
  };

  async function save() {
    if (busy) return;
    setBusy(true);
    try {
      if (intoDeal) {
        const saved = await api.deal.put(picked);
        qc.setQueryData(["deal"], saved);
        await nav({ to: "/deal" });
        return;
      }
      const created = await api.selections.create({ title: todayTitle(), itemIds: picked });
      drafts.clear(PICK_OWN_KEY);
      await qc.invalidateQueries({ queryKey: ["selections"] });
      await nav({ to: "/set/$id", params: { id: created.id } });
    } finally {
      setBusy(false);
    }
  }

  const topLevel = cats.filter((c) => !c.parentId);
  return (
    <div>
      <div className="screen-head">
        <button className="icon-btn" aria-label={t("back")} onClick={() => history.back()}>{Ic.back()}</button>
        <h1 className="display" style={{ fontSize: "var(--fs-title)", lineHeight: "var(--lh-title)" }}>{replacing ? t("pick.replaceTitle") : t("pick.title")}</h1>
      </div>
      <div className="field">
        <label htmlFor="pick-search" className="visually-hidden">{t("search")}</label>
        <input id="pick-search" className="input" type="search" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div role="tablist" className="chip-row" style={{ marginBottom: "var(--sp-2)" }}>
        {!replacing && <button role="tab" className="chip" aria-selected={tab === "selected"} onClick={() => setTab("selected")}>{t("pick.selected", { n: picked.length })}</button>}
        <button role="tab" className="chip" aria-selected={tab === "all"} onClick={() => setTab("all")}>{t("all")}</button>
        {topLevel.map((c) => (
          <button key={c.id} role="tab" className="chip" aria-selected={tab === c.id} onClick={() => setTab(c.id)}>{catLabel(c, t)}</button>
        ))}
      </div>

      <div role="group" aria-label={replacing ? t("pick.replaceTitle") : t("pick.title")} style={{ paddingBottom: replacing ? 0 : 96 }}>
        {rows.map((item) => {
          const on = !replacing && picked.includes(item.id);
          // replace mode: what's already in the set can't be picked twice
          const full = replacing ? existing.includes(item.id) || busy : !on && picked.length >= max;
          return (
            <button key={item.id} type="button" className="pick-row" aria-pressed={replacing ? undefined : on} aria-disabled={full} disabled={full} onClick={() => toggle(item.id)}>
              {!replacing && <span className={`tick ${on ? "on" : full ? "disabled" : ""}`}>{on && Ic.check({ size: 14 })}</span>}
              <Cover item={item} size="s" />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.title}</span>
                <span className="meta">{metaLine(item, cats, t)}</span>
              </span>
              {replacing && <span style={{ color: "var(--ink-3)", display: "inline-flex" }}>{Ic.chevron({ size: 16 })}</span>}
            </button>
          );
        })}
        {rows.length === 0 && <p className="meta" style={{ padding: "var(--sp-5) 0", textAlign: "center" }}>{t("shelf.noMatch")}</p>}
      </div>

      {!replacing && (
        <div className="sticky-bottom">
          <button className="btn btn-primary btn-block" disabled={picked.length === 0 || busy} onClick={save}>
            {intoDeal ? t("done") : t("deal.save")}
          </button>
        </div>
      )}
    </div>
  );
}
